import { memo, useState } from "react"
import { Button, CircularProgress, Stack } from "@mui/material"
import { useParams } from "react-router"
import { Bound } from "lib/@components/binding/Bound"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { NoResults } from "lib/@components/no-results/no-results"
import { Repeat } from "lib/@components/repeat"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { getAuthoringDraftRestorePoints } from "./controller/get-authoring-draft-restore-points"
import { restoreAuthoringDraftRestorePoint } from "./controller/restore-authoring-draft-restore-point"
import { resetAuthoringDraft } from "./controller/reset-authoring-draft"
import { AuthoringDraftRestorePointRow } from "./authoring-draft-restore-point-row"
import { RestorePointAdded } from "./schedule-editor-event-definitions.mjs"

const TAB_MIN_HEIGHT = 302

export const AuthoringDraftHistoryTab = memo(function AuthoringDraftHistoryTab() {
    const { projectId, scheduleId } = useParams()
    const { reloadAuthoringDraftFromServer } = useScheduleEditorActions()
    const [busy, setBusy] = useState(false)
    // `withInitialValue(IS_LOADING)` guarantees the tuple is defined on first render so the
    // destructure captures a real `refresh` function. Otherwise the tuple would be undefined
    // initially, fall back to a placeholder, and the event-subscription closure would
    // permanently point at the placeholder via useCallback memoisation.
    const [restorePoints, refreshRestorePoints] = getAuthoringDraftRestorePoints.useResultsOnce.withRefetch(
        { projectId, scheduleId },
        withInitialValue(IS_LOADING)
    )
    RestorePointAdded(createDraftId(projectId, scheduleId)).useEvent(refreshRestorePoints)

    if (!projectId || !scheduleId) return null
    if (restorePoints === IS_LOADING) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", minHeight: TAB_MIN_HEIGHT }}>
                <CircularProgress />
            </Stack>
        )
    }

    return (
        <Bound onRestoreDraftHistoryPoint={handleRestorePoint} draftHistoryBusy={busy}>
            <Stack spacing={2} sx={{ height: "100%", minHeight: 0, pt: 1.5 }}>
                <Stack direction="row" justifyContent="flex-end">
                    <Button size="small" color="primary" disabled={busy} onClick={handleResetToPublishedBasis}>
                        Reset to published basis
                    </Button>
                </Stack>
                <If truthy={(restorePoints ?? []).length > 0}>
                    <Stack spacing={0.5} sx={{ minHeight: 0, overflowY: "auto" }}>
                        <Repeat list={restorePoints ?? []} item={<AuthoringDraftRestorePointRow />} />
                    </Stack>
                    <Else>
                        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, minHeight: 0 }}>
                            <NoResults mt={0}>No saved restore points yet.</NoResults>
                        </Stack>
                    </Else>
                </If>
            </Stack>
        </Bound>
    )

    async function handleRestorePoint(restorePoint) {
        if (!projectId || !scheduleId || !restorePoint?.id) return

        try {
            setBusy(true)
            const restored = await restoreAuthoringDraftRestorePoint({
                projectId,
                scheduleId,
                restorePointId: restorePoint.id,
            })
            if (!restored) {
                throw new Error("Failed to restore draft state")
            }
            await reloadAuthoringDraftFromServer?.()
            refreshRestorePoints()
            successSnackbar("Draft restored")
        } catch (error) {
            console.error("Failed to restore draft state", error)
            errorSnackbar("Failed to restore draft state")
        } finally {
            setBusy(false)
        }
    }

    async function handleResetToPublishedBasis() {
        if (!projectId || !scheduleId) return

        try {
            setBusy(true)
            const reset = await resetAuthoringDraft({ projectId, scheduleId })
            if (!reset) {
                throw new Error("Failed to reset draft")
            }
            await reloadAuthoringDraftFromServer?.()
            refreshRestorePoints()
            successSnackbar("Draft reset to published basis")
        } catch (error) {
            console.error("Failed to reset authoring draft", error)
            errorSnackbar("Failed to reset draft")
        } finally {
            setBusy(false)
        }
    }
})
